import type { ChatMessage, ThreadNotice } from './chat-types';
import { OPTIFLOW_SEED_THREADS } from './optiflow-thread-seed';

export type SeedThread = {
  id: string;
  title: string;
  pinned: boolean;
  updatedAt: number;
  messages: ChatMessage[];
  /** Set when the thread belongs to a project. The project-scoped chat
   *  sidebar (`/projects/<slug>/chat`) filters to these; the global `/`
   *  Pinned/Recents lists show every thread, matching ai-foundation. */
  projectSlug?: string;
};

const now = Date.now();
const hour = 60 * 60 * 1000;

const CONNECTOR_AUTH_MESSAGES: ChatMessage[] = [
  {
    id: 1,
    who: 'user',
    time: '14:02',
    body: 'What does this scaffold ship with?',
  },
  {
    id: 2,
    who: 'bot',
    time: '14:02',
    body:
      'Three-panel chrome (sidebar · scene · assistant), design-system primitives, tokenised theming, build-doc drawer, and comments overlay.',
    parts: [
      {
        type: 'tool',
        verb: 'Read',
        preview: 'docs/logic/workspace/chat-rail.md',
        detail: 'Opened the chat rail logic doc for context.',
      },
      {
        type: 'markdown',
        content:
          '**Shipped surfaces**\n\n- Sidebar system with thread history\n- Docked assistant rail\n- Admin + settings spine pages',
      },
      {
        type: 'artifact',
        title: 'Foundation overview',
        kind: 'document',
      },
    ],
    citations: [{ label: 'DOC-001' }, { label: 'DOC-002' }],
  },
  {
    id: 3,
    who: 'user',
    time: '14:05',
    body: 'How do I add a module?',
  },
  {
    id: 4,
    who: 'bot',
    time: '14:05',
    body:
      'Copy modules/overview as the starting point: Scene.tsx plus its Scene.transform.ts sibling, a route in app/router.tsx, and an anchor in comments/anchors.ts.',
    citations: [{ label: 'DOC-002' }],
  },
];

function genericTurns(title: string): ChatMessage[] {
  return [
    {
      id: 1,
      who: 'user',
      time: '09:12',
      body: `Can you summarise ${title.toLowerCase()}?`,
    },
    {
      id: 2,
      who: 'bot',
      time: '09:12',
      body: `Here is a short recap of ${title} — this is seeded mock content for the rail history overlay.`,
    },
    {
      id: 3,
      who: 'user',
      time: '09:14',
      body: 'What should we do next?',
    },
    {
      id: 4,
      who: 'bot',
      time: '09:14',
      body: 'Pick one follow-up action and I can draft bullets or a status note.',
    },
  ];
}

/** Per-thread dismissible notices — keyed by thread id. */
export const SEED_THREAD_NOTICES: Record<string, ThreadNotice[]> = {
  'thread-3': [
    {
      id: 'notice-connector-auth',
      kind: 'connector-auth',
      severity: 'info',
      body: 'GitHub connector needs re-authorization before it can read private repos.',
      actionHint: 'Open Settings → Connectors to finish OAuth.',
    },
  ],
};

export const SEED_THREADS: SeedThread[] = [
  ...OPTIFLOW_SEED_THREADS,
  {
    id: 'thread-1',
    title: 'Onboarding checklist',
    pinned: true,
    updatedAt: now - hour,
    messages: genericTurns('Onboarding checklist'),
  },
  {
    id: 'thread-2',
    title: 'Q3 roadmap brainstorm',
    pinned: true,
    updatedAt: now - 3 * hour,
    messages: genericTurns('Q3 roadmap brainstorm'),
  },
  {
    id: 'thread-3',
    title: 'Connector auth flow',
    pinned: false,
    updatedAt: now - 5 * hour,
    messages: CONNECTOR_AUTH_MESSAGES,
  },
  {
    id: 'thread-4',
    title: 'Settings parity notes',
    pinned: false,
    updatedAt: now - 24 * hour,
    messages: genericTurns('Settings parity notes'),
  },
  {
    id: 'thread-5',
    title: 'Admin panel wireframes',
    pinned: false,
    updatedAt: now - 48 * hour,
    messages: genericTurns('Admin panel wireframes'),
  },
  {
    id: 'thread-6',
    title: 'New chat',
    pinned: false,
    updatedAt: now - 72 * hour,
    messages: genericTurns('New chat'),
  },
  {
    id: 'thread-beta-1',
    title: 'Beta scope sketch',
    pinned: false,
    updatedAt: now - 3 * 24 * hour,
    messages: genericTurns('Beta scope sketch'),
    projectSlug: 'beta',
  },
  {
    id: 'thread-gamma-1',
    title: 'Gamma field checklist',
    pinned: false,
    updatedAt: now - 26 * hour,
    messages: genericTurns('Gamma field checklist'),
    projectSlug: 'gamma',
  },
  {
    id: 'thread-gamma-2',
    title: 'Gamma sensor calibration',
    pinned: false,
    updatedAt: now - 40 * hour,
    messages: genericTurns('Gamma sensor calibration'),
    projectSlug: 'gamma',
  },
];
